// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { IconWrapper } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import cartLineSharedStyles from "@sc/App/SharedStyles/CartLineSharedStyles";
import { productListLineStyles } from "@sc/Overrides/Widgets/MyListsDetails/MyListsDetailsProductListLine";
import { color, createLinkIconStyles, displayFlexOnPrint, xScreen } from "@sc/XDL/Utilities";
import { mergeWithCss } from "@sc/XDL/Utilities";
import { css } from "styled-components";

productListLineStyles.selectedCheckbox = {
    css: css`
        @media print {
            display: none;
        }
        && > [role="checkbox"] {
            background-color: ${color("common.ui17")};
        }
        margin: 12px 20px 0 0;
    `,
};

productListLineStyles.sortOrderGridItem = {
    width: 12,
    css: css`
        @media print {
            display: none;
        }
    `,
};
productListLineStyles.sortOrderTextField = {
    labelPosition: "left",
    sizeVariant: "small",
    cssOverrides: {
        formInputWrapper: css`
            width: 60px;
        `,
    },
    labelProps: {
        css: css`
            width: auto;
        `,
    },
};
productListLineStyles.imageSection = {
    css: css`
        margin-right: 20px;
        background-color: ${color("common.white")};
        width: 120px;
        height: 120px;
        ${xScreen("lt", "xs")} {
            width: 280px;
            height: 280px;
            margin: 0 auto 12px;
        }
        @media print {
            width: 100px;
            height: 100px;
            margin-right: 16px;
            margin-left: 0;
        }
    `,
};
productListLineStyles.productImageStyles = {
    linkWrappingImage: {
        css: css`
            width: 120px;
            height: 120px;
            justify-content: center;
            ${xScreen("lt", "xs")} {
                width: 100%;
                height: 100%;
                max-height: 280px;
            }
            @media print {
                width: 100px;
                height: 100px;
            }
        `,
    },
};
productListLineStyles.infoContainer = {
    gap: 10,
    css: css`
        width: 100%;
    `,
};
productListLineStyles.productDetailsContainer = {
    gap: 10,
    css: css`
        width: 100%;
    `,
};
productListLineStyles.brandGridItem = {
    width: 12,
    css: css`
        padding-bottom: 0;
    `,
};
productListLineStyles.descriptionGridItem = {
    width: 12,
    printWidth: 6,
    css: css`
        flex-direction: column;
        padding-top: 0;
    `,
};
productListLineStyles.productDescriptionStyles = {
    ...cartLineSharedStyles.productDescription,
};
productListLineStyles.productPartNumbersStyles = {
    erpNumberGridItem: {
        css: css`
            flex-direction: row;
            ${displayFlexOnPrint}
        `,
    },
    erpNumberLabelText: { variant: "legend" },
    customerNameGridItem: {
        css: css`
            flex-direction: row;
            ${displayFlexOnPrint}
        `,
    },
    customerNameLabelText: { variant: "legend" },
    manufacturerItemGridItem: {
        css: css`
            flex-direction: row;
            ${displayFlexOnPrint}
        `,
    },
    manufacturerItemLabelText: { variant: "legend" },
};
productListLineStyles.partNumbersGridItem = { width: 12 };
productListLineStyles.addToCartActionsWrapper = {
    css: css`
        display: flex;
        width: 100%;
        flex-direction: column;
    `,
};
productListLineStyles.productPriceStyles = {
    wrapper: {
        css: css`
            margin-bottom: 4px;
            ${xScreen("lt", "sm")} {
                align-items: flex-start;
            }
        `,
    },
};
productListLineStyles.quantityBreakPricing = {
    viewLink: {
        typographyProps: {
            size: 12,
        },
        css: css`
            margin-bottom: 8px;
        `,
    },
};
productListLineStyles.addToCartButton = {
    mergeCss: true,
    sizeVariant: "small",
    css: css`
        flex: 1;
        max-width: 100%;
        width: 100%;
        height: 28px;
    `,
};
productListLineStyles.addToCartWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        flex-basis: 100%;
    `,
};
productListLineStyles.quantityAddtoCartWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        width: 100%;
        gap: 8px;
        @media print {
            flex-direction: column;
            align-items: flex-start;
            gap: 5px;
        }
    `,
};
productListLineStyles.updateQuantityLink = {
    typographyProps: {
        size: 11,
        weight: "bold",
        css: css`
            line-height: 15px;
        `,
    },
    css: css`
        margin-block: 0 5px;
    `,
};
productListLineStyles.quantityUpdatedWrapper = {
    css: css`
        line-height: 1;
        margin-bottom: 5px;
        @media print {
            display: none;
        }
    `,
};
productListLineStyles.quantityUpdatedIcon = {
    src: Check,
    color: "success",
    size: 11,
    css: css`
        margin-right: 3px;
    `,
};
productListLineStyles.quantityUpdatedText = {
    size: 11,
    weight: "bold",
    color: "success",
};
productListLineStyles.notesGridItem = {
    width: [12, 12, 6, 6, 6],
    css: css`
        display: flex;
        flex-direction: column;
        ${xScreen("gte", "lg")} {
            padding-left: 10px;
        }
    `,
};
productListLineStyles.actionsGridItem = {
    width: [12, 12, 6, 6, 6],
    css: css`
        min-width: 317px;
    `,
};
productListLineStyles.notesStyles = {
    size: 14,
    weight: "normal",
    color: "common.ui4",
    css: css`
        letter-spacing: 0;
        line-height: 19px;
    `,
};
productListLineStyles.restrictedMessageGridItem = {
    width: 12,
};
productListLineStyles.restrictedRemoveItemLinkWrapper = {
    css: css`
        display: flex;
        flex: 1;
        justify-content: flex-end;
    `,
};
productListLineStyles.removeItemLinkDesktop = {
    ...createLinkIconStyles("X"),
};
productListLineStyles.emptySpaceWrapper = {
    css: css`
        width: 100px;
        height: 100px;
        ${xScreen("lte", "sm")} {
            width: 280px;
            height: 280px;
        }
    `,
};
productListLineStyles.restrictedDescriptionText = {
    css: css`
        font-weight: bold;
    `,
};
productListLineStyles.container = {
    css: css`
        margin: 0 auto;
        height: 100%;
        width: 100%;
        position: relative;
        display: flex;
        flex-wrap: nowrap;
        align-items: stretch;
        padding: 0;
        background-color: ${color("common.white")};
        ${xScreen("lt", "md")} {
            flex-direction: column;
        }
        @media print {
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            width: 100%;
            max-width: 100%;
        }
    `,
};
productListLineStyles.info = {
    width: [12, 12, 6, 6, 6],
    css: css`
        margin-right: auto;
        ${xScreen("lte", "sm")} {
            margin-bottom: 10px;
        }
    `,
};
productListLineStyles.priceAndActions = {
    width: [12, 12, 6, 6, 6],
    css: css`
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        padding: 0;
    `,
};
productListLineStyles.actions = {
    css: css`
        margin-top: 10px;
        display: flex;
        justify-content: space-between;
        width: 100%;
        @media print {
            display: none;
        }
        ${xScreen("lte", "sm")} {
            flex-direction: column-reverse;
        }
    `,
};
productListLineStyles.productAvailabilityStyles = {
    container: {
        css: css`
            margin-bottom: 8px;
        `,
        offsetCss: css`
            & > div:empty {
                display: none;
            }
        `,
    },
    errorWrapper: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            ${displayFlexOnPrint}
        `,
    },
};
productListLineStyles.removeItemLink = {
    src: X,
    size: 16,
    color: "common.ui8",
    css: css`
        position: absolute;
        right: 0;
        top: 8px;
        &:hover {
            cursor: pointer;
        }
        ${xScreen("lt", "sm")} {
            right: 0;
        }
        @media print {
            display: none;
        }
    `,
};
productListLineStyles.addNotesWrapper = {
    width: 12,
    css: css`
        margin-top: 16px;
        padding: 5px;
    `,
};
productListLineStyles.removeItemWrapper = {
    css: css`
        display: flex;
        justify-content: space-between;
        width: 100%;
        button {
            margin-top: 0;
        }
    `,
};
productListLineStyles.productUnitOfMeasureSelectStyles = {
    labelProps: {
        css: css`
            display: none;
            @media print {
                display: block;
            }
        `,
    },
    cssOverrides: {
        inputSelect: css`
            margin: 0;
            height: 28px;
        `,
        formField: css`
            width: 111px;
            ${IconWrapper} {
                background: none;
            }
        `,
    },
};

productListLineStyles.productQuantityOrderedStyles = {
    wrapper: {
        css: css`
            width: 70px;
            min-width: 70px;
        `,
    },
};

productListLineStyles.actionsWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        @media print {
            display: none;
        }
        ${xScreen("lt", "md")} {
            width: 100%;
            flex-direction: row;
        }
    `,
};

productListLineStyles.deleteLink = {
    icon: {
        iconProps: {
            src: X,
            size: 12,
            css: css`
                /* margin: 0 auto; */
                margin-right: 6px;
            `,
        },
    },
    typographyProps: {
        size: 12,
    },
    css: css`
        display: flex;
        justify-content: center;
        margin-left: auto;
        &:focus {
            outline: none;
        }
    `,
};

productListLineStyles.deleteItemWrapper = {
    css: css`
        align-self: flex-end;
        margin-top: 28px;
        display: flex;
        width: 100%;
        justify-content: space-between;
        align-items: flex-start;
    `,
};

productListLineStyles.lineWrapper = {
    css: css`
        margin: 0 auto;
        height: 100%;
        width: 100%;
        position: relative;
        display: flex;
        flex-wrap: nowrap;
        align-items: flex-start;
        padding: 12px 0;
        background-color: ${color("common.white")};
        ${xScreen("lt", "xs")} {
            flex-direction: column;
        }
        @media print {
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            width: 100%;
            max-width: 100%;
        }
    `,
};

productListLineStyles.infoGridItem = {
    width: 12,
};

productListLineStyles.infoGridContainer = {
    gap: 10,
    offsetCss: css`
        &&& {
            @media print {
                display: flex;
            }
        }
    `,
};

productListLineStyles.leftInfoGridItem = {
    width: [12, 12, 12, 8, 8],
    css: css`
        display: flex;
        flex-direction: column;
        gap: 7px;
    `,
};

productListLineStyles.rightInfoGridItem = {
    width: [12, 12, 5, 4, 4],
};

productListLineStyles.editNotesLink = {
    icon: {
        iconProps: {
            size: 12,
            css: css`
                margin-right: 6px;
            `,
        },
    },
    typographyProps: {
        size: 12,
    },
    css: css`
        display: flex;
        justify-content: center;
    `,
};

productListLineStyles.showMoreLink = {
    typographyProps: { size: 12 },
    css: css`
        margin-top: 5px;
    `,
};

productListLineStyles.createdOnText = {
    size: 12,
    italic: true,
    weight: "normal",
    color: "common.ui4",
};

productListLineStyles.purchasedDateGridItem = {
    width: 12,
    css: css`
        flex-direction: column;
        padding: 0 0 10px 0;
    `,
};

productListLineStyles.purchasedDateLabelText = {
    ...mergeWithCss(productListLineStyles.purchasedDateLabelText, {
        css: css`
            margin-bottom: 0;
        `,
    }),
};

productListLineStyles.editableNotesTextArea = {
    css: css`
        &&&& {
            overflow: auto;
            display: flex;
        }
        min-width: 100%;
        max-width: 100%;
    `,
};
